import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { useLang } from '../../../i18n';
import { useToast } from '../../../components/Toast';
import { get, post } from '../../../services/api';
import Card from '../../../components/Card';
import Badge from '../../../components/Badge';
import Button from '../../../components/Button';
import Input from '../../../components/Input';
import Select from '../../../components/Select';
import Modal from '../../../components/Modal';
import Skeleton from '../../../components/Skeleton';

interface ContactChannel {
  public_id: string;
  channel_type: 'email' | 'phone' | 'telegram' | 'whatsapp';
  value: string;
  is_verified: boolean;
}

const CHANNEL_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'whatsapp', label: 'WhatsApp' },
];

export default function ProfileChannels() {
  const { t } = useLang();
  const toast = useToast();
  const [items, setItems] = useState<ContactChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('email');
  const [value, setValue] = useState('');

  const load = async () => {
    try {
      const res = await get<{ results: ContactChannel[] }>('/me/contact-channels/');
      setItems(res.results || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const add = async () => {
    try {
      await post('/me/contact-channels/', { channel_type: type, value });
      toast.success(t('profile.saved'));
      setOpen(false);
      setValue('');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    }
  };

  if (loading) return <Skeleton height={200} />;

  return (
    <div className="profile-channels">
      {items.map((c) => (
        <Card key={c.public_id} padding="md">
          <div className="profile-channels__item">
            <div className="profile-channels__info">
              <span className="profile-channels__type">{c.channel_type}</span>
              <span className="profile-channels__value">{c.value}</span>
            </div>
            {c.is_verified ? (
              <Badge variant="success">{t('profile.channels.verified')}</Badge>
            ) : (
              <Badge variant="warning">—</Badge>
            )}
          </div>
        </Card>
      ))}
      <div>
        <Button variant="secondary" size="md" iconLeft={<Plus size={14} />} onClick={() => setOpen(true)}>
          {t('profile.channels.add')}
        </Button>
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title={t('profile.channels.add')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-3)' }}>
          <Select label="Type" value={type} onChange={setType} options={CHANNEL_OPTIONS} />
          <Input label="Value" value={value} onChange={(e) => setValue(e.target.value)} />
          <Button variant="primary" size="md" onClick={add}>{t('common.save')}</Button>
        </div>
      </Modal>
    </div>
  );
}
